import { Badge, Tooltip } from '@mantine/core';
import { PiShieldCheck, PiShieldWarning, PiShield } from 'react-icons/pi';
import dayjs from 'dayjs';
import { payerKycStatus, type TPayer } from '@/models/payers';

/**
 * Three-state KYC badge derived from the expiry date alone:
 * valid (green/yellow/red by proximity), expired (red), missing (gray).
 */
const noClampStyles = {
  root: { maxWidth: 'none' },
  label: { overflow: 'visible', textOverflow: 'clip' },
} as const;

export function KycStateBadge({
  kycExpiresAt,
  size = 'sm',
}: {
  kycExpiresAt: string | null | undefined;
  size?: string;
}) {
  const status = payerKycStatus(kycExpiresAt ?? null);
  if (status === 'missing') {
    return (
      <Badge
        size={size}
        variant="light"
        color="gray"
        styles={noClampStyles}
        leftSection={<PiShield size={10} />}
      >
        KYC missing
      </Badge>
    );
  }
  if (status === 'expired') {
    return (
      <Badge
        size={size}
        variant="light"
        color="red"
        styles={noClampStyles}
        leftSection={<PiShieldWarning size={10} />}
      >
        KYC expired {dayjs(kycExpiresAt).format('DD.MM.YYYY')}
      </Badge>
    );
  }
  const days = dayjs(kycExpiresAt).diff(dayjs(), 'day');
  const color = days < 7 ? 'red' : days < 30 ? 'yellow' : 'green';
  return (
    <Badge
      size={size}
      variant="light"
      color={color}
      styles={noClampStyles}
      leftSection={<PiShieldCheck size={10} />}
    >
      KYC valid till {dayjs(kycExpiresAt).format('DD.MM.YYYY')}
    </Badge>
  );
}

/**
 * Inline KYC status indicator for a payer.
 * - verified: payer has valid (non-expired) KYC
 * - expired: payer exists but KYC has lapsed
 * - none: no payer attached
 * Reuses the `kycValid`/`kycExpiresAt` already on the payer payload.
 */
export function PayerKycBadge({ payer }: { payer: TPayer | null | undefined }) {
  if (!payer) {
    return (
      <Tooltip label="No payer attached — an invoice cannot be issued without a KYC-verified payer">
        <Badge
          size="xs"
          variant="light"
          color="gray"
          leftSection={<PiShield size={10} />}
        >
          No KYC
        </Badge>
      </Tooltip>
    );
  }

  if (!payer.kycExpiresAt) {
    return (
      <Tooltip label="Payer has not passed KYC yet — verification will be collected during offer acceptance">
        <Badge
          size="xs"
          variant="light"
          color="gray"
          leftSection={<PiShield size={10} />}
        >
          KYC missing
        </Badge>
      </Tooltip>
    );
  }

  if (payer.kycValid) {
    return (
      <Tooltip
        label={
          payer.kycExpiresAt
            ? `KYC verified — valid until ${dayjs(payer.kycExpiresAt).format('DD.MM.YYYY')}`
            : 'KYC verified'
        }
      >
        <Badge
          size="xs"
          variant="light"
          color="teal"
          leftSection={<PiShieldCheck size={10} />}
        >
          KYC verified
        </Badge>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      label={
        payer.kycExpiresAt
          ? `KYC expired ${dayjs(payer.kycExpiresAt).format('DD.MM.YYYY')} — re-verify before issuing`
          : 'KYC expired — re-verify before issuing'
      }
    >
      <Badge
        size="xs"
        variant="light"
        color="red"
        leftSection={<PiShieldWarning size={10} />}
      >
        KYC expired
      </Badge>
    </Tooltip>
  );
}
